// Panel de configuración del proveedor tecnológico de facturación electrónica.
// Solo uno puede estar activo; activarlo desactiva los demás.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, MenuItem, Paper, Radio, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { toast } from '../../../utils/toast';
import {
  FacturacionElectronicaApi, ProveedorFe, ProveedorPayload,
} from '../../../services/api/facturacionElectronicaApi';

const VACIO: ProveedorPayload = {
  Codigo: 'matias', Nombre: '', BaseUrl: '', Token: '', Email: '', Password: '',
  ClientId: '', ClientSecret: '', Ambiente: 'SANDBOX', Activo: false,
};

/** Proveedores que autentican con OAuth2 y necesitan client id/secret. */
const USA_OAUTH = (codigo: string) => codigo === 'factus';

const PanelProveedores: React.FC = () => {
  const [proveedores, setProveedores] = useState<ProveedorFe[]>([]);
  const [disponibles, setDisponibles] = useState<string[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState<ProveedorPayload>(VACIO);

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      const [lista, codigos] = await Promise.all([
        FacturacionElectronicaApi.listarProveedores(),
        FacturacionElectronicaApi.proveedoresDisponibles(),
      ]);
      setProveedores(lista);
      setDisponibles(codigos);
    } catch {
      toast.error('No se pudieron cargar los proveedores');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => { setForm(VACIO); setAbierto(true); };

  const abrirEdicion = (p: ProveedorFe) => {
    // Las credenciales no viajan de vuelta: se dejan vacías y solo se envían
    // si el usuario escribe una nueva.
    setForm({
      Id: p.Id, Codigo: p.Codigo, Nombre: p.Nombre, BaseUrl: p.BaseUrl,
      Email: p.Email ?? '', Token: '', Password: '',
      ClientId: '', ClientSecret: '', RangoNumeracionId: p.RangoNumeracionId ?? undefined,
      Ambiente: p.Ambiente, Activo: p.Activo,
    });
    setAbierto(true);
  };

  const campo = (k: keyof ProveedorPayload) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const guardar = async () => {
    if (!form.Nombre.trim() || !form.BaseUrl.trim()) {
      toast.warning('El nombre y la URL base son obligatorios');
      return;
    }
    try {
      setGuardando(true);
      const dto: ProveedorPayload = { ...form };
      if (!dto.Token) delete dto.Token;
      if (!dto.Password) delete dto.Password;
      if (!dto.ClientId) delete dto.ClientId;
      if (!dto.ClientSecret) delete dto.ClientSecret;
      await FacturacionElectronicaApi.guardarProveedor(dto);
      toast.success(form.Id ? 'Proveedor actualizado' : 'Proveedor creado');
      setAbierto(false);
      cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo guardar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  const activar = async (p: ProveedorFe) => {
    if (p.Activo) return;
    const res = await Swal.fire({
      title: `¿Activar ${p.Nombre}?`,
      text: 'Las próximas facturas se emitirán con este proveedor y los demás quedarán inactivos.',
      icon: 'question', showCancelButton: true,
      confirmButtonText: 'Sí, activar', cancelButtonText: 'Cancelar', reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    try {
      await FacturacionElectronicaApi.activarProveedor(p.Id);
      toast.success(`${p.Nombre} es ahora el proveedor activo`);
      cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo activar');
    }
  };

  const eliminar = async (p: ProveedorFe) => {
    const res = await Swal.fire({
      title: '¿Eliminar proveedor?', text: `Se eliminará la configuración de ${p.Nombre}.`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar', reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    try {
      await FacturacionElectronicaApi.eliminarProveedor(p.Id);
      toast.success('Proveedor eliminado');
      cargar();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'No se pudo eliminar');
    }
  };

  const activo = proveedores.find((p) => p.Activo);

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800}>Proveedor tecnológico</Typography>
          <Typography variant="body2" color="text.secondary">
            Quién transmite las facturas a la DIAN. Solo uno puede estar activo.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={abrirNuevo}>
          Agregar proveedor
        </Button>
      </Stack>

      {!cargando && activo?.Ambiente === 'PRODUCCION' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{activo.Nombre}</strong> está en <strong>PRODUCCIÓN</strong>: las facturas
          que emitas tendrán validez fiscal real ante la DIAN.
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : proveedores.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" fontWeight={600}>Sin proveedores configurados</Typography>
          <Typography variant="body2" color="text.secondary">
            Configura al menos uno para poder emitir facturas electrónicas.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Activo</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>URL base</TableCell>
                <TableCell>Credencial</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proveedores.map((p) => (
                <TableRow key={p.Id} hover selected={p.Activo}>
                  <TableCell padding="checkbox">
                    <Tooltip title={p.Activo ? 'Proveedor activo' : 'Activar este proveedor'}>
                      <span><Radio checked={p.Activo} onChange={() => activar(p)} /></span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{p.Nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.Codigo}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.BaseUrl}</Typography>
                  </TableCell>
                  <TableCell>
                    {p.TokenEnmascarado ? (
                      <Chip size="small" label={`Token ${p.TokenEnmascarado}`} variant="outlined" />
                    ) : p.TienePassword ? (
                      <Chip size="small" label="Email + contraseña" variant="outlined" />
                    ) : (
                      <Chip size="small" color="warning" label="Sin credencial" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={p.Ambiente}
                      color={p.Ambiente === 'PRODUCCION' ? 'success' : 'default'}
                      variant={p.Ambiente === 'PRODUCCION' ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => abrirEdicion(p)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={p.Activo ? 'No se puede eliminar el activo' : 'Eliminar'}>
                      <span>
                        <IconButton size="small" color="error" disabled={p.Activo}
                          onClick={() => eliminar(p)}><DeleteIcon fontSize="small" /></IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={abierto} onClose={() => setAbierto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {form.Id ? 'Editar proveedor' : 'Nuevo proveedor'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Implementación" value={form.Codigo}
                onChange={campo('Codigo')}
                helperText="Proveedores soportados por el sistema">
                {disponibles.map((c) => (
                  <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nombre visible" value={form.Nombre}
                onChange={campo('Nombre')} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="URL base de la API" value={form.BaseUrl}
                onChange={campo('BaseUrl')} required
                placeholder="https://sandbox-api.matias-api.com/api/ubl2.1" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Token de acceso" value={form.Token ?? ''}
                onChange={campo('Token')} type="password"
                helperText={form.Id
                  ? 'Déjalo vacío para conservar el token actual'
                  : 'Vía recomendada. Si no tienes token, usa email y contraseña.'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email (opcional)" value={form.Email ?? ''}
                onChange={campo('Email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Contraseña (opcional)" type="password"
                value={form.Password ?? ''} onChange={campo('Password')}
                helperText={form.Id ? 'Vacío = conservar' : undefined} />
            </Grid>
            {USA_OAUTH(form.Codigo) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Client ID" type="password"
                    value={form.ClientId ?? ''} onChange={campo('ClientId')}
                    helperText={form.Id ? 'Vacío = conservar' : 'Requerido por Factus'} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Client Secret" type="password"
                    value={form.ClientSecret ?? ''} onChange={campo('ClientSecret')}
                    helperText={form.Id ? 'Vacío = conservar' : 'Requerido por Factus'} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth type="number" label="ID del rango de numeración"
                    value={form.RangoNumeracionId ?? ''} onChange={campo('RangoNumeracionId')}
                    helperText="Factus lleva su propio consecutivo; indica cuál rango usar (lo consultas en su panel)." />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Ambiente" value={form.Ambiente}
                onChange={campo('Ambiente')}>
                <MenuItem value="SANDBOX">Sandbox (pruebas)</MenuItem>
                <MenuItem value="PRODUCCION">Producción</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="¿Activar al guardar?"
                value={form.Activo ? '1' : '0'}
                onChange={(e) => setForm((f) => ({ ...f, Activo: e.target.value === '1' }))}
                helperText="Desactiva a los demás">
                <MenuItem value="1">Sí</MenuItem>
                <MenuItem value="0">No</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAbierto(false)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {guardando ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PanelProveedores;
